/**
 * Queue order config at `global/parkingQueue`: the one order the parking
 * queue is shown in, agreed once per instance and edited by an admin under
 * Instansinställningar → Parkeringskö. It only sets the view and defines what
 * counts as a deviation when a lease from the queue skips someone; it never blocks or
 * reorders data. A missing node means the defaults. Unlike the other
 * `global/` children this one is admin-writable, not superadmin-only.
 */
export const QUEUE_GROUPINGS = ['internalExternal', 'spotsHeld', 'none'] as const;
export type QueueGrouping = typeof QUEUE_GROUPINGS[number];

export const QUEUE_SORTS = ['queueDate', 'spotsHeld', 'lastTouched'] as const;
export type QueueSort = typeof QUEUE_SORTS[number];

export interface ParkingQueueConfig {
    grouping: QueueGrouping;
    sort: QueueSort;
}

export const PARKING_QUEUE_CONFIG_PATH = 'global/parkingQueue';

export const DEFAULT_PARKING_QUEUE_CONFIG: ParkingQueueConfig = {
    grouping: 'internalExternal',
    sort: 'queueDate',
};

export const QUEUE_GROUPING_LABELS: Record<QueueGrouping, string> = {
    internalExternal: 'Interna först, sedan externa',
    spotsHeld: 'Efter antal platser idag (0 · 1 · 2+)',
    none: 'Ingen gruppering',
};

export const QUEUE_SORT_LABELS: Record<QueueSort, string> = {
    queueDate: 'Längst kötid överst',
    spotsHeld: 'Minst antal platser idag överst, sedan kötid',
    lastTouched: 'Längst sedan senast rörd överst',
};

/** Narrows an arbitrary database value to a config, applying defaults for anything missing or unknown. */
export function parseParkingQueueConfig(value: unknown): ParkingQueueConfig {
    const d = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
    const grouping = (QUEUE_GROUPINGS as readonly unknown[]).includes(d.grouping)
        ? (d.grouping as QueueGrouping)
        : DEFAULT_PARKING_QUEUE_CONFIG.grouping;
    const sort = (QUEUE_SORTS as readonly unknown[]).includes(d.sort)
        ? (d.sort as QueueSort)
        : DEFAULT_PARKING_QUEUE_CONFIG.sort;
    return { grouping, sort };
}

/** "Interna först, sedan externa · Längst kötid överst" — the bar's statement of the standard order. */
export function describeQueueOrder(config: ParkingQueueConfig): string {
    const parts = [QUEUE_SORT_LABELS[config.sort]];
    if (config.grouping !== 'none') parts.unshift(QUEUE_GROUPING_LABELS[config.grouping]);
    return parts.join(' · ');
}
